import { Col, DatePicker, Form, Input, Radio, Row, Select, Checkbox, Divider } from "antd";
import React from "react";
import { adminContext } from "../../Common/PageRoute";
import PracticeSubmissionHoldsService, { ApplicationPracticeSubmissionType } from "../../Services/PracticeSubmissionHoldsService";
import dayjs from "dayjs";
import SAModal from "../../Common/Custom Components/Ant design/SAModal";
import { submissionHoldListDataType } from "./SubmissionHoldList";
import { useParams } from "react-router-dom";
import "../../Components/Practices/Practices.css";
import { Utils } from "../../Utilities/Utils";
import { IPractices } from "../../Context/PracticeContext";

interface submissionHoldFormProps {
  record: submissionHoldListDataType | null;
  onDismiss?: (saved: boolean) => void;
  practiceOptions: any[];
  modelOpen: boolean;
}

const SubmissionHoldForm = ({ record, onDismiss, practiceOptions, modelOpen }: submissionHoldFormProps) => {
  const [loading, setLoading] = React.useState(false);
  const { id }: any = useParams();
  const practiceId: number = Number(id);
  const { openNotification } = React.useContext(adminContext);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (!record) return;

    const filingMap: any = {};
    (record.filingTypes || []).forEach((f: number) => (filingMap[f] = true));

    const existingIds = record.practices?.map((p: any) => p.id) || [];

    form.setFieldsValue({
      warningMessage: record.warningMessage || "",
      practices: existingIds.length ? existingIds : ["all"],
      type: record.type,
      dateRange:
        record.from && record.to ? [dayjs(record.from), dayjs(record.to)] : [],
      filingTypes: filingMap,
    });
  }, [record, form]);

  const OnSaveClick = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const warningMessage = values.warningMessage;

      const selected = values.practices || [];
      const allIds = practiceOptions.map((p) => p.value);

      const isAllSelected =
        selected.includes("all") || selected.length === allIds.length;

      const practices = isAllSelected
        ? practiceOptions.map((p) => ({ id: p.value }))
        : selected
          .map((id: number) => {
            const found = practiceOptions.find((p: any) => p.value === id);
            return found ? { id: found.value } : null;
          })
          .filter(Boolean);

      const filingTypes: number[] = [];
      Object.keys(values.filingTypes || {}).forEach((key) => {
        if (values.filingTypes[key]) filingTypes.push(Number(key));
      });

      const payload = {
        practices,
        warningMessage,
        type: values.type,
        from: values.dateRange?.[0]?.toISOString(),
        to: values.dateRange?.[1]?.toISOString(),
        filingTypes,
        recordStatus: 0, // same logic as before
      };

      let res;
      if (record?.id) {
        res = await PracticeSubmissionHoldsService.update(record.id, payload);
      } else {
        res = await PracticeSubmissionHoldsService.save(undefined, payload);
      }

      if (res?.status) {
        openNotification("success", "Practice submission hold saved successfully");
        onDismiss?.(true);
      } else if (res?.message) {
        openNotification("error", res?.message || "Failed to save submission hold");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SAModal
      title={record ? "Edit submission hold" : "Add submission hold"}
      open={modelOpen}
      width={800}
      isLoading={loading}
      okText="Save"
      cancelText="Cancel"
      onOk={OnSaveClick}
      onCancel={() => onDismiss?.(false)}
      destroyOnClose
    >
      <Form form={form}>
        <Col >
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label="Practices"
                name="practices"
                rules={[{ required: true, message: "Select at least one practice" }]}
                initialValue={practiceId ? [practiceId] : undefined}
                className="custom-form-item"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Select
                  // className="select-height-28"
                  showSearch
                  optionFilterProp="label"
                  mode="multiple"
                  allowClear
                  disabled={practiceId > 0}
                  style={{ height: 28 }}
                  maxTagCount="responsive"
                  placeholder="Select Practices"
                  options={[
                    { label: "All Practices", value: "all" },
                    ...practiceOptions.map((p: IPractices) => ({
                      value: p.value,
                      label: p.label + " (ID: " + p.value + ")",
                    }))
                  ]}
                  onChange={(values: string[]) => {
                    if (values.includes("all")) {
                      const allIds = practiceOptions.map((p) => p.value);
                      form.setFieldsValue({ practices: allIds });
                      return;
                    }
                    // else {
                    //   form.setFieldsValue({ practices: values });
                    // }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label="Message"
                name="warningMessage"
                rules={[{ required: true, message: "Message" }, { validator: Utils.validateWhiteSpace }]}
                className="custom-form-item"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Input.TextArea placeholder="Message" />
              </Form.Item>
            </Col>
            {/* </Row>
          <Row gutter={[10, 10]} style={{ marginTop: -14 }}> */}
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: "Select type" }]}
                className="custom-form-item"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Radio.Group>
                  <Radio value={0}>Error</Radio>
                  <Radio value={1}>Warning</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12} >
              <Form.Item
                label="Date & Time"
                name="dateRange"
                rules={[{ required: true, message: "Date is required" }]}
                className="custom-form-item"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <DatePicker.RangePicker
                  style={{ height: 28, width: "100%" }}
                  showTime={{ format: "HH:mm", hideDisabledOptions: true }}
                  format="DD/MM/YYYY HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[0, 4]} style={{ marginTop: 10 }}>
            <Divider
              orientation="left"
              orientationMargin="0"
              style={{ margin: "8px 0px", fontWeight: 650 }}
            >
              <p >
                Submission Filing Types
              </p>
            </Divider>
            {[
              { key: ApplicationPracticeSubmissionType.AR_CH, label: "AR (CH)" },
              { key: ApplicationPracticeSubmissionType.CT, label: "CT 600" },
              { key: ApplicationPracticeSubmissionType.SA100, label: "SA100" },
              { key: ApplicationPracticeSubmissionType.SA800, label: "SA800" },
              { key: ApplicationPracticeSubmissionType.SA900, label: "SA900" },
              { key: ApplicationPracticeSubmissionType.CIS, label: "CIS" },
              { key: ApplicationPracticeSubmissionType.EPS, label: "EPS" },
              { key: ApplicationPracticeSubmissionType.FPS, label: "FPS" },
              { key: ApplicationPracticeSubmissionType.P11D, label: "P11D" },
              { key: ApplicationPracticeSubmissionType.CS, label: "CS" },
              { key: ApplicationPracticeSubmissionType.CS_Additional, label: "CS Additional" },
              { key: ApplicationPracticeSubmissionType.VAT, label: "VAT" },
            ].map((item) => (
              <Col sm={12} xs={24} lg={6} key={item.key}>
                <Form.Item name={["filingTypes", item.key]} valuePropName="checked" noStyle>
                  <Checkbox>{item.label}</Checkbox>
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Form.Item shouldUpdate>
            {() => (
              <Form.Item
                name="filingTypes"
                rules={[
                  {
                    validator: () => {
                      const data = form.getFieldValue("filingTypes") || {};
                      if (!Object.values(data).some(Boolean))
                        return Promise.reject("Select at least one filing type");
                      return Promise.resolve();
                    },
                  },
                ]}
                noStyle
              >
                <div />
              </Form.Item>
            )}
          </Form.Item>

          {/* <Row className="px-3 pt-3">
          <Col span={24} style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" onClick={OnSaveClick} disabled={loading}>
              <SaveOutlined /> Save
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => onDismiss?.(false)}>
              <CloseOutlined /> Cancel
            </Button>
          </Col>
        </Row> */}
        </Col>
      </Form>
    </SAModal>

  );
};

export default SubmissionHoldForm;
